import { useEffect, useRef, useState } from "react";
import { api } from "../../api";
import { ErrorNote, Loading } from "../../components/Feedback";
import { Btn, Card, Field, Input, Textarea } from "../../components/Shared";
import { rememberFocus } from "../../focus";
import { AskDialog, LEGEND, SuccessNote } from "./parts";

// Management -> Announcements (task 2026-10-21 G, GA): an admin writes one e-mail, ticks any
// number of groups (each per-project group optionally narrowed to chosen projects), previews
// the deduplicated list of people, and sends. Each person gets their own message and a bell entry.
//
//   GET  /api/admin/announcements/options     groups, projects, placeholders
//   POST /api/admin/announcements/preview     {groups, subject, body, important} -> {count, emailed,
//                                             people, subject, body, problems}: the server resolves
//   POST /api/admin/announcements             the same -> {recipients, emailed, bell_only}; 429
//                                             within a minute of your last one
//   GET/POST/PUT/DELETE /api/admin/announcement-templates[/{id}]   named texts to start from
//
// The send resolves the groups again on the server; the list shown is only what the preview saw.
// Without a connected e-mail server (options/preview `email_available: false`) the card warns and
// the announcement reaches the bell only; the send's `no_email` says how many lost the e-mail.

const BLANK = { subject: "", body: "" };
const box = { border: "1px solid #e5e7eb", borderRadius: 8, padding: "10px 13px" };

function payload(chosen, draft, important) {
  return {
    groups: Object.entries(chosen).map(([group, ids]) => ({ group, project_ids: ids })),
    subject: draft.subject, body: draft.body, important,
  };
}

function GroupPicker({ options, chosen, onChange }) {
  const toggle = key => {
    const next = { ...chosen };
    if (key in next) delete next[key]; else next[key] = [];
    onChange(next);
  };
  const toggleProject = (key, id) => {
    const have = chosen[key] || [];
    onChange({ ...chosen, [key]: have.includes(id) ? have.filter(x => x !== id) : [...have, id] });
  };
  return (
    <fieldset style={{ border: "none", margin: 0, padding: 0 }}>
      <legend style={LEGEND}>Recipients</legend>
      <p style={{ margin: "4px 0 8px", fontSize: 12, color: "#4b5563" }}>
        Tick any groups; a person in several is reached once. Only active, confirmed accounts with
        an e-mail address are reached.
      </p>
      <div style={{ display: "grid", gap: 6 }}>
        {options.groups.map(g => {
          const on = g.key in chosen;
          const projects = g.kind === "data" ? options.projects.filter(p => p.hosted) : options.projects;
          return (
            <div key={g.key}>
              <label style={{ display: "inline-flex", gap: 8, alignItems: "center", fontSize: 14 }}>
                <input type="checkbox" checked={on} onChange={() => toggle(g.key)} />
                {g.label}
              </label>
              {on && g.per_project && (
                <fieldset style={{ border: "none", margin: "4px 0 6px 26px", padding: 0 }}>
                  <legend style={{ fontSize: 12, color: "#4b5563", padding: 0 }}>
                    {g.label}: only in these projects (none ticked: {g.kind === "data" ? "every hosted project" : "every project"})
                  </legend>
                  {projects.length === 0 && <span style={{ fontSize: 12, color: "#6b7280" }}>No {g.kind === "data" ? "hosted " : ""}project yet.</span>}
                  <div style={{ display: "flex", gap: "4px 14px", flexWrap: "wrap", marginTop: 4 }}>
                    {projects.map(p => (
                      <label key={p.id} style={{ display: "inline-flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                        <input type="checkbox" checked={(chosen[g.key] || []).includes(p.id)}
                               onChange={() => toggleProject(g.key, p.id)} />
                        <span data-break>{p.name}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}

function People({ preview }) {
  return (
    <div data-testid="announcement-preview" aria-live="polite" style={{ marginTop: 14 }}>
      <p style={{ margin: "0 0 8px", fontSize: 14, fontWeight: 600 }}>
        {preview.count === 0 ? "These groups reach nobody."
          : `${preview.count} ${preview.count === 1 ? "person" : "people"}, ${preview.emailed} by e-mail`
            + (preview.email_available === false ? " (e-mail is not connected: the bell only)" : "")}
      </p>
      {preview.problems.length > 0 && (
        <ul style={{ margin: "0 0 8px", paddingLeft: 18, fontSize: 12, color: "#b91c1c" }}>
          {preview.problems.map(p => <li key={p}>{p}</li>)}
        </ul>
      )}
      {preview.count > 0 && (
        <>
          <div style={{ ...box, background: "#f9fafb", fontSize: 13, marginBottom: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Subject: {preview.subject}</div>
            <pre style={{ margin: 0, whiteSpace: "pre-wrap", fontFamily: "inherit" }}>{preview.body}</pre>
          </div>
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            <table className="responsive-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <caption style={{ textAlign: "left", fontSize: 12, color: "#4b5563", paddingBottom: 4 }}>
                Who it reaches (the first one's text is shown above)
              </caption>
              <thead>
                <tr style={{ textAlign: "left", color: "#4b5563" }}>
                  <th scope="col" style={{ padding: "4px 8px" }}>Name</th>
                  <th scope="col" style={{ padding: "4px 8px" }}>E-mail</th>
                  <th scope="col" style={{ padding: "4px 8px" }}>Gets</th>
                </tr>
              </thead>
              <tbody>
                {preview.people.map(p => (
                  <tr key={p.id} style={{ borderTop: "1px solid #f3f4f6" }}>
                    <td data-label="Name" style={{ padding: "4px 8px" }}>{p.full_name || p.username}</td>
                    <td data-label="E-mail" data-break style={{ padding: "4px 8px" }}>{p.email}</td>
                    <td data-label="Gets" style={{ padding: "4px 8px" }}>{p.channel === "email" ? "E-mail and bell" : "Bell only"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Templates({ templates, current, draft, onPick, onChanged, setError, setNotice }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const chosen = templates.find(t => t.id === current);

  useEffect(() => { setName(chosen ? chosen.name : ""); }, [chosen]);

  const act = async (fn, message) => {
    setError(null); setNotice(null); setBusy(true);
    try { await fn(); setNotice(message); } catch (e) { setError(e); } finally { setBusy(false); }
  };
  const saveNew = () => act(async () => {
    const t = await api.post("/admin/announcement-templates", { name, ...draft });
    await onChanged(t.id);
  }, `Template “${name.trim()}” saved.`);
  const update = () => act(async () => {
    await api.put(`/admin/announcement-templates/${chosen.id}`, { name, ...draft });
    await onChanged(chosen.id);
  }, `Template “${name.trim()}” updated.`);
  const remove = () => act(async () => {
    setAsking(false);
    await api.del(`/admin/announcement-templates/${chosen.id}`);
    await onChanged(null);
  }, `Template “${chosen.name}” deleted. Announcements already sent keep their text.`);

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <Field label="Start from" hint="A saved template fills the subject and the text; nothing is sent.">
        <select value={current ?? ""} onChange={e => onPick(e.target.value ? Number(e.target.value) : null)}
                style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, fontFamily: "inherit", background: "#fff" }}>
          <option value="">A blank announcement</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </Field>
      <div data-field-row style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Field label="Template name" style={{ flex: "1 1 220px" }}>
          <Input value={name} onChange={e => setName(e.target.value)} />
        </Field>
        <Btn variant="secondary" onClick={saveNew} disabled={busy || !name.trim()}>Save as new template</Btn>
        {chosen && <Btn variant="secondary" onClick={update} disabled={busy || !name.trim()}>Update template</Btn>}
        {chosen && <Btn variant="secondary" onClick={() => setAsking(true)} disabled={busy}>Delete template</Btn>}
      </div>
      {asking && (
        <AskDialog title={`Delete the template “${chosen.name}”?`} confirmLabel="Delete template" busy={busy}
                   onConfirm={remove} onClose={() => setAsking(false)}>
          <p style={{ margin: 0, fontSize: 14 }}>Announcements already sent from it keep their text.</p>
        </AskDialog>
      )}
    </div>
  );
}

export default function AnnouncementsCard() {
  const [options, setOptions] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loadError, setLoadError] = useState(null);
  const [current, setCurrent] = useState(null);
  const [draft, setDraft] = useState(BLANK);
  const [chosen, setChosen] = useState({});
  const [important, setImportant] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const buttonsRef = useRef(null);

  const loadTemplates = () => api.get("/admin/announcement-templates").then(setTemplates);

  useEffect(() => {
    let live = true;
    Promise.all([api.get("/admin/announcements/options"), api.get("/admin/announcement-templates")])
      .then(([o, t]) => { if (live) { setOptions(o); setTemplates(t); } })
      .catch(e => { if (live) setLoadError(e); });
    return () => { live = false; };
  }, []);

  // A preview describes one selection and one text: any change makes it stale.
  useEffect(() => { setPreview(null); }, [chosen, draft, important]);

  if (loadError) return <ErrorNote error={loadError} />;
  if (!options) return <Loading label="Loading…" />;

  const pick = id => {
    setCurrent(id);
    const t = templates.find(x => x.id === id);
    setDraft(t ? { subject: t.subject, body: t.body } : BLANK);
  };
  const changed = async id => { await loadTemplates(); setCurrent(id); };

  const run = async (what, fn) => {
    setError(null); setNotice(null);
    const restore = rememberFocus();
    setBusy(what);
    try { await fn(); } catch (e) { setError(e); } finally {
      setBusy(null);
      restore(() => buttonsRef.current && buttonsRef.current.querySelector("button"));
    }
  };
  const doPreview = () => run("preview", async () => {
    setPreview(await api.post("/admin/announcements/preview", payload(chosen, draft, important)));
  });
  const send = () => run("send", async () => {
    setAsking(false);
    const r = await api.post("/admin/announcements", payload(chosen, draft, important));
    setNotice(`Sent to ${r.recipients} ${r.recipients === 1 ? "person" : "people"}: ${r.emailed} by e-mail `
              + `and the bell, ${r.bell_only} in the bell only`
              + (r.no_email ? ` (${r.no_email} of them because e-mail is not connected).` : ".")
              + (r.emailed ? " E-mails leave within a minute." : ""));
    setPreview(null);
  });

  const nothing = Object.keys(chosen).length === 0;
  const canSend = preview && preview.count > 0 && preview.problems.length === 0;

  return (
    <Card>
      <h2 style={{ margin: "0 0 6px", fontSize: 18, fontWeight: 700 }}>Send an announcement</h2>
      <p style={{ margin: "0 0 14px", fontSize: 13, color: "#4b5563" }}>
        One e-mail to chosen groups. Each person gets their own message (nobody sees the other
        addresses) and an entry in their bell. People may choose the bell only for announcements in
        their notification settings; tick “Important” to e-mail everyone anyway. Plain text;
        {" "}<code>{"{{placeholder}}"}</code> is replaced per person.
      </p>
      {options.email_available === false && (
        <p role="note" data-testid="no-email-warning"
           style={{ margin: "0 0 14px", padding: "10px 13px", borderRadius: 8, fontSize: 13,
                    background: "#fffbeb", border: "1px solid #fcd34d", color: "#78350f" }}>
          <strong>E-mail is not connected</strong> (Management → E-mail). An announcement sent now reaches
          everyone's bell in the portal only; nobody gets an e-mail.
        </p>
      )}
      <ErrorNote error={error} />
      <SuccessNote>{notice}</SuccessNote>

      <div style={{ display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", alignItems: "start" }}>
        <div style={{ display: "grid", gap: 12 }}>
          <Templates templates={templates} current={current} draft={draft} onPick={pick} onChanged={changed}
                     setError={setError} setNotice={setNotice} />
          <Field label="Subject">
            <Input value={draft.subject} onChange={e => setDraft(d => ({ ...d, subject: e.target.value }))} />
          </Field>
          <Field label="Text">
            <Textarea value={draft.body} rows={10} onChange={e => setDraft(d => ({ ...d, body: e.target.value }))} />
          </Field>
          <ul aria-label="Placeholders" style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#374151" }}>
            {options.placeholders.map(p => (
              <li key={p.name}><code>{`{{${p.name}}}`}</code> — {p.description}</li>
            ))}
          </ul>
        </div>
        <div style={{ display: "grid", gap: 12 }}>
          <GroupPicker options={options} chosen={chosen} onChange={setChosen} />
          <label style={{ display: "inline-flex", gap: 8, alignItems: "flex-start", fontSize: 14 }}>
            <input type="checkbox" checked={important} onChange={e => setImportant(e.target.checked)} style={{ marginTop: 3 }} />
            <span>Important — e-mail everyone, whatever they chose (maintenance, an outage)</span>
          </label>
        </div>
      </div>

      <div ref={buttonsRef} style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
        <Btn variant="secondary" onClick={doPreview} disabled={!!busy || nothing}>
          {busy === "preview" ? "Checking…" : "Preview recipients"}
        </Btn>
        <Btn onClick={() => setAsking(true)} disabled={!!busy || !canSend}>
          {busy === "send" ? "Sending…" : "Send"}
        </Btn>
      </div>
      {!preview && !nothing && (
        <p style={{ margin: "8px 0 0", fontSize: 12, color: "#4b5563" }}>Preview the recipients before you send.</p>
      )}
      {preview && <People preview={preview} />}
      {asking && (
        <AskDialog title={`Send to ${preview.count} ${preview.count === 1 ? "person" : "people"}?`}
                   confirmLabel="Send announcement" busy={busy === "send"} onConfirm={send} onClose={() => setAsking(false)}>
          <p style={{ margin: 0, fontSize: 14 }}>
            “{preview.subject}” goes to {preview.emailed} by e-mail and to everyone's bell
            {preview.email_available === false ? " (e-mail is not connected)" : ""}. It cannot be
            called back.
          </p>
        </AskDialog>
      )}
    </Card>
  );
}
